import { PlaceOfInsuranceForm } from "@/components/address/PlaceOfInsuranceForm";
import LabelWithPopover from "@/components/form/labels/LabelWithPopover";
import LabelWithTooltip from "@/components/form/labels/LabelWithTooltip";
import { InputNumber } from "@/components/InputNumber";
import { selectStandardProps } from "@/utils/componentUtils";
import { resolveFormValidationError } from "@/utils/formUtils";
import { isDefined } from "@/utils/utils";
import { validations } from "@/utils/validationUtils";
import { Card, Checkbox, Col, Form, Row, Select } from "antd";
import { CheckboxChangeEvent } from "antd/lib/checkbox";
import { FormInstance } from "antd/lib/form";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { rowGutter } from "../../../../../../../common/constants";
import { FieldConstraintViolation } from "../../../../../../../common/types";
import ClientDrawerForm from "../../../../../../client/components/drawers/ClientDrawerForm";
import ClientSearchInput from "../../../../../../client/components/search/ClientSearchInput";
import { ClientFormStage, ClientFormType, ClientSearchActionType, ClientType } from "../../../../../../client/enums";
import { useClientSearch } from "../../../../../../client/hooks";
import { Client, LegalClient, NaturalClient, SelfEmployedClient } from "../../../../../../client/types";
import { AreaType, BuildingLevel, BuildingState } from "../../../enums";
import { RealtyCalc } from "../../../types";

const colSpan = 4;
const YEAR_NUMBER_INPUT_MIN = 1900;
const YEAR_NUMBER_INPUT_MAX = dayjs().year();

interface Props {
  form: FormInstance<RealtyCalc>;
  policyHolder?: Partial<Client>;
  clientsViolationErrors: Map<ClientFormType, FieldConstraintViolation[]>;
  onPolicyHolderChange: (client?: NaturalClient | SelfEmployedClient | LegalClient) => void;
  onPolicyHolderViolationErrorsDelete: () => void;
}

export const RealtyCalcGeneralSection = ({ form, policyHolder, ...props }: Props) => {
  const { t } = useTranslation();
  const clientSearch = useClientSearch();

  const [clientFormOpen, setClientFormOpen] = useState<boolean>(false);
  const [storedPolicyHolderFormStage, setStoredPolicyHolderFormStage] = useState<ClientFormStage | undefined>(
    policyHolder ? ClientFormStage.SELECTED : undefined,
  );
  // Only the NEW stage (searched, but no such client exists) applies while there is no policy holder.
  const policyHolderFormStage =
    policyHolder || storedPolicyHolderFormStage === ClientFormStage.NEW ? storedPolicyHolderFormStage : undefined;

  const state = Form.useWatch(["generalBuildingData", "state"], form);
  const constructionYear = Form.useWatch(["generalBuildingData", "constructionYear"], form);

  useEffect(() => {
    if (
      clientSearch.result.keyword &&
      clientSearch.result.keyword === form.getFieldValue(["clientsData", "policyHolderIdentifier"])
    ) {
      if (clientSearch.result.data) {
        setClientFormOpen(true);
        setStoredPolicyHolderFormStage(ClientFormStage.EXISTING);
        if (
          clientSearch.result.data.type === ClientType.NATURAL ||
          clientSearch.result.data.type === ClientType.SELF_EMPLOYED
        ) {
          props.onPolicyHolderChange({
            ...clientSearch.result.data,
            previousIdentityCardNumber: (clientSearch.result.data as NaturalClient).identityCardNumber,
          } as NaturalClient);
        } else {
          props.onPolicyHolderChange(clientSearch.result.data as LegalClient);
        }
      } else {
        setStoredPolicyHolderFormStage(ClientFormStage.NEW);
      }
    }
  }, [clientSearch.result]);

  useEffect(() => {
    return () => {
      clientSearch.onResultDelete();
    };
  }, []);

  const handleClientSearchActionClick = (_: ClientFormType, actionType: ClientSearchActionType) => {
    switch (actionType) {
      case ClientSearchActionType.CREATE:
      case ClientSearchActionType.UPDATE:
        setClientFormOpen(true);
        break;
      case ClientSearchActionType.DELETE:
        form.setFieldsValue({ clientsData: { policyHolderIdentifier: undefined } });
        setStoredPolicyHolderFormStage(undefined);
        props.onPolicyHolderViolationErrorsDelete();
        props.onPolicyHolderChange(undefined);
        break;
    }
  };

  const handleClientSearchSubmit = (value: string) => {
    form
      .validateFields([["clientsData", "policyHolderIdentifier"]])
      .then(() => clientSearch.onSearch({ keyword: value }))
      .catch(resolveFormValidationError);
  };

  const handleClientSearchChange = (value: string) => {
    if (storedPolicyHolderFormStage) {
      setStoredPolicyHolderFormStage(undefined);
    }
    if (policyHolder) {
      props.onPolicyHolderChange(undefined);
    }
    if (props.clientsViolationErrors.has(ClientFormType.POLICY_HOLDER)) {
      props.onPolicyHolderViolationErrorsDelete();
    }
    handleClientSearchSubmit(value);
  };

  const handleClientFormSubmit = (client: NaturalClient | SelfEmployedClient | LegalClient) => {
    setClientFormOpen(false);
    setStoredPolicyHolderFormStage(ClientFormStage.SELECTED);
    props.onPolicyHolderViolationErrorsDelete();
    props.onPolicyHolderChange(client);
  };

  const getPolicyHolderFormIdentifier = (): string => {
    return form.getFieldValue(["clientsData", "policyHolderIdentifier"]);
  };

  const handleBuildingStateChange = (buildingState: BuildingState) => {
    if (buildingState === BuildingState.UNDER_CONSTRUCTION) {
      form.setFieldsValue({
        generalBuildingData: { constructionYear: undefined, reconstructionYear: undefined },
        householdInsuranceEnabled: false,
      });
    }
  };

  const handleConstructionYearChange = (constructionYear: string | number | null) => {
    if (!constructionYear) {
      form.setFieldsValue({ generalBuildingData: { reconstructionYear: undefined } });
    }
  };

  const handleNumberOfDamagesInLastThreeYearsChange = (damagesInThreeYears: number | null) => {
    if (!isDefined(damagesInThreeYears)) {
      return;
    }

    const damagesInFiveYears = form.getFieldValue(["generalBuildingData", "numberOfDamagesInLastFiveYears"]);
    const damagesInTenYears = form.getFieldValue(["generalBuildingData", "numberOfDamagesInLastTenYears"]);

    if (damagesInThreeYears > damagesInFiveYears) {
      form.setFieldValue(["generalBuildingData", "numberOfDamagesInLastFiveYears"], damagesInThreeYears);
    }

    if (damagesInThreeYears > damagesInTenYears) {
      form.setFieldValue(["generalBuildingData", "numberOfDamagesInLastTenYears"], damagesInThreeYears);
    }
  };

  const handleNumberOfDamagesInLastFiveYearsChange = (damagesInFiveYears: number | null) => {
    if (!isDefined(damagesInFiveYears)) {
      return;
    }

    const damagesInThreeYears = form.getFieldValue(["generalBuildingData", "numberOfDamagesInLastThreeYears"]);
    const damagesInTenYears = form.getFieldValue(["generalBuildingData", "numberOfDamagesInLastTenYears"]);

    if (damagesInFiveYears < damagesInThreeYears) {
      form.setFieldValue(["generalBuildingData", "numberOfDamagesInLastThreeYears"], damagesInFiveYears);
    }

    if (damagesInFiveYears > damagesInTenYears) {
      form.setFieldValue(["generalBuildingData", "numberOfDamagesInLastTenYears"], damagesInFiveYears);
    }
  };

  const handleNumberOfDamagesInLastTenYearsChange = (damagesInTenYears: number | null) => {
    if (!isDefined(damagesInTenYears)) {
      return;
    }

    const damagesInThreeYears = form.getFieldValue(["generalBuildingData", "numberOfDamagesInLastThreeYears"]);
    const damagesInFiveYears = form.getFieldValue(["generalBuildingData", "numberOfDamagesInLastFiveYears"]);

    if (damagesInTenYears < damagesInFiveYears) {
      form.setFieldValue(["generalBuildingData", "numberOfDamagesInLastFiveYears"], damagesInTenYears);
    }

    if (damagesInFiveYears < damagesInThreeYears) {
      form.setFieldValue(["generalBuildingData", "numberOfDamagesInLastTenYears"], damagesInTenYears);
    }
  };

  const handlePermanentlyOccupiedChanged = (event: CheckboxChangeEvent) => {
    if (!event.target.checked) {
      form.setFieldsValue({
        householdInsuranceData: {
          artworksInsuranceAmount: undefined,
          valuablesInsuranceAmount: undefined,
        },
      });
    }
  };

  return (
    <>
      <Card type="inner" className="card-box" title={t("calc.realty.sections.generalDataAndClient")}>
        <Row gutter={rowGutter}>
          <ClientSearchInput<ClientFormType>
            formItemProps={{
              name: ["clientsData", "policyHolderIdentifier"],
              label: t("calc.realty.attrs.clientsData.policyHolderIndexPinCrn"),
              rules: [validations.notBlank, validations.pinOrCrn, validations.adultByPin],
              validateFirst: true,
            }}
            formStage={policyHolderFormStage}
            formType={ClientFormType.POLICY_HOLDER}
            processedType={ClientFormType.POLICY_HOLDER}
            client={policyHolder}
            violationErrors={props.clientsViolationErrors}
            inProgress={clientSearch.inProgress}
            inputColSpan={colSpan + 1}
            clientNameColSpan={colSpan + 3}
            onActionClick={handleClientSearchActionClick}
            onSearch={handleClientSearchSubmit}
            onChange={handleClientSearchChange}
          />
        </Row>

        <Row gutter={rowGutter}>
          <Col span={colSpan}>
            <Form.Item
              name={["generalBuildingData", "state"]}
              label={t("calc.realty.enums.buildingState._label")}
              rules={[validations.notNull]}
            >
              <Select
                {...selectStandardProps}
                options={Object.keys(BuildingState).map((state) => ({
                  value: state,
                  label: t("calc.realty.enums.buildingState." + state),
                }))}
                onChange={handleBuildingStateChange}
              />
            </Form.Item>
          </Col>

          <Col span={colSpan}>
            <Form.Item
              name={["generalBuildingData", "constructionYear"]}
              label={
                <LabelWithTooltip
                  label={t("calc.realty.attrs.generalBuildingData.constructionYear")}
                  tooltip={t("calc.realty.helpers.constructionYear")}
                />
              }
              rules={[
                state === BuildingState.FINISHED || state === BuildingState.FINISHED_UNDER_RECONSTRUCTION
                  ? validations.notNull
                  : validations.none,
                validations.minNumber(YEAR_NUMBER_INPUT_MIN),
                validations.maxNumber(YEAR_NUMBER_INPUT_MAX),
              ]}
            >
              <InputNumber
                onChange={handleConstructionYearChange}
                disabled={state === BuildingState.UNDER_CONSTRUCTION}
              />
            </Form.Item>
          </Col>

          <Col span={colSpan}>
            <Form.Item
              name={["generalBuildingData", "reconstructionYear"]}
              label={
                <LabelWithTooltip
                  label={t("calc.realty.attrs.generalBuildingData.reconstructionYear")}
                  tooltip={t("calc.realty.helpers.reconstructionYear")}
                />
              }
              rules={[
                validations.minNumber(constructionYear ? constructionYear + 1 : YEAR_NUMBER_INPUT_MIN),
                validations.maxNumber(YEAR_NUMBER_INPUT_MAX),
              ]}
            >
              <InputNumber disabled={state === BuildingState.UNDER_CONSTRUCTION || !constructionYear} />
            </Form.Item>
          </Col>

          <Col span={colSpan}>
            <Form.Item
              name={["generalBuildingData", "areaType"]}
              label={
                <LabelWithPopover
                  label={t("calc.realty.attrs.generalBuildingData.areaTypeLabel")}
                  popoverTitle={t("calc.realty.helpers.areaTypeDesc.title")}
                  popoverContent={
                    <div style={{ maxWidth: 750 }}>
                      <div>
                        <h4>{t("calc.realty.helpers.areaTypeDesc.risk.title")}</h4>
                        <span>{t("calc.realty.helpers.areaTypeDesc.risk.text1")}</span>
                        <ul>
                          {[2, 3, 4, 5].map((value) => (
                            <li key={value}>{t("calc.realty.helpers.areaTypeDesc.risk.text" + value)}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  }
                />
              }
              rules={[validations.none]}
            >
              <Select
                {...selectStandardProps}
                allowClear
                options={Object.keys(AreaType).map((type) => ({
                  value: type,
                  label: t("calc.realty.enums.areaType." + type),
                }))}
              />
            </Form.Item>
          </Col>

          <Col span={colSpan}>
            <Form.Item
              name={["generalBuildingData", "level"]}
              label={t("calc.realty.enums.buildingLevel._label")}
              rules={[validations.none]}
            >
              <Select
                {...selectStandardProps}
                allowClear
                options={Object.keys(BuildingLevel).map((level) => ({
                  value: level,
                  label: t("calc.realty.enums.buildingLevel." + level),
                }))}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={rowGutter}>
          <Col span={colSpan}>
            <Form.Item
              name={["generalBuildingData", "numberOfDamagesInLastThreeYears"]}
              label={t("calc.realty.attrs.generalBuildingData.numberOfDamagesInLastThreeYears")}
              rules={[validations.notNull, validations.minNumber(0)]}
            >
              <InputNumber onChange={handleNumberOfDamagesInLastThreeYearsChange} />
            </Form.Item>
          </Col>

          <Col span={colSpan}>
            <Form.Item
              name={["generalBuildingData", "numberOfDamagesInLastFiveYears"]}
              label={t("calc.realty.attrs.generalBuildingData.numberOfDamagesInLastFiveYears")}
              rules={[validations.notNull, validations.minNumber(0)]}
            >
              <InputNumber onChange={handleNumberOfDamagesInLastFiveYearsChange} />
            </Form.Item>
          </Col>

          <Col span={colSpan}>
            <Form.Item
              name={["generalBuildingData", "numberOfDamagesInLastTenYears"]}
              label={t("calc.realty.attrs.generalBuildingData.numberOfDamagesInLastTenYears")}
              rules={[validations.notNull, validations.minNumber(0)]}
            >
              <InputNumber onChange={handleNumberOfDamagesInLastTenYearsChange} />
            </Form.Item>
          </Col>

          <Col span={colSpan}>
            <Form.Item
              name={["generalBuildingData", "numberOfFloodsInLastTenYears"]}
              label={t("calc.realty.attrs.generalBuildingData.numberOfFloodsInLastTenYears")}
              rules={[validations.notNull, validations.minNumber(0)]}
            >
              <InputNumber />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={rowGutter}>
          <Col span={colSpan}>
            <Form.Item
              name={["generalBuildingData", "inCity"]}
              className="form-item-without-label"
              valuePropName="checked"
              rules={[validations.none]}
              initialValue={true}
            >
              <Checkbox>{t("calc.realty.attrs.generalBuildingData.inCity")}</Checkbox>
            </Form.Item>
          </Col>

          <Col span={colSpan}>
            <Form.Item
              name={["generalBuildingData", "permanentlyOccupied"]}
              className="form-item-without-label"
              valuePropName="checked"
              rules={[validations.none]}
              initialValue={true}
            >
              <Checkbox onChange={handlePermanentlyOccupiedChanged}>
                <LabelWithPopover
                  label={t("calc.realty.attrs.generalBuildingData.permanentlyOccupied")}
                  popoverTitle={t("calc.realty.helpers.permanentlyOccupiedDesc.title")}
                  popoverContent={
                    <table className="data-table-view wide-margins" style={{ maxWidth: 650 }}>
                      <tbody>
                        {[1, 2, 3, 4, 5, 6, 7, 8].map((value) => (
                          <tr key={value}>
                            <td>{t("calc.realty.helpers.permanentlyOccupiedDesc.institution" + value)}</td>
                            <td>{t("calc.realty.helpers.permanentlyOccupiedDesc.value" + value)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  }
                />
              </Checkbox>
            </Form.Item>
          </Col>

          <Col span={colSpan + 2}>
            <Form.Item
              name={["generalBuildingData", "occupiedByDisabledPerson"]}
              className="form-item-without-label"
              valuePropName="checked"
              rules={[validations.none]}
              initialValue={false}
            >
              <Checkbox style={{ maxHeight: 22 }}>
                <LabelWithTooltip
                  label={t("calc.realty.attrs.generalBuildingData.occupiedByDisabledPerson")}
                  tooltip={t("calc.realty.helpers.occupiedByDisabledPerson")}
                />
              </Checkbox>
            </Form.Item>
          </Col>

          <Col span={colSpan + 1}>
            <Form.Item
              name={["generalBuildingData", "business"]}
              className="form-item-without-label"
              valuePropName="checked"
              rules={[validations.none]}
              initialValue={false}
            >
              <Checkbox>{t("calc.realty.attrs.generalBuildingData.business")}</Checkbox>
            </Form.Item>
          </Col>

          <Col span={colSpan}>
            <Form.Item
              name={["generalBuildingData", "historical"]}
              className="form-item-without-label"
              valuePropName="checked"
              rules={[validations.none]}
              initialValue={false}
            >
              <Checkbox>{t("calc.realty.attrs.generalBuildingData.historical")}</Checkbox>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={rowGutter}>
          <Col flex="none">
            <Form.Item
              name={["generalBuildingData", "motionSensor"]}
              valuePropName="checked"
              rules={[validations.none]}
              initialValue={false}
            >
              <Checkbox>
                <LabelWithTooltip
                  label={t("calc.realty.attrs.generalBuildingData.motionSensor")}
                  tooltip={t("calc.realty.helpers.motionSensorDesc")}
                />
              </Checkbox>
            </Form.Item>
          </Col>

          <Col flex="none">
            <Form.Item
              name={["generalBuildingData", "fireSensor"]}
              valuePropName="checked"
              rules={[validations.none]}
              initialValue={false}
            >
              <Checkbox>
                <LabelWithTooltip
                  label={t("calc.realty.attrs.generalBuildingData.fireSensor")}
                  tooltip={t("calc.realty.helpers.fireSensorDesc")}
                />
              </Checkbox>
            </Form.Item>
          </Col>

          <Col flex="none">
            <Form.Item
              name={["generalBuildingData", "waterSensor"]}
              valuePropName="checked"
              rules={[validations.none]}
              initialValue={false}
            >
              <Checkbox>
                <LabelWithTooltip
                  label={t("calc.realty.attrs.generalBuildingData.waterSensor")}
                  tooltip={t("calc.realty.helpers.waterSensorDesc")}
                />
              </Checkbox>
            </Form.Item>
          </Col>
        </Row>

        <PlaceOfInsuranceForm
          form={form}
          rootNamePath={["generalBuildingData", "placeOfInsurance"]}
          label={t("calc.realty.attrs.generalBuildingData.placeOfInsurance._label")}
          required
          buildingFinished={
            !state || state === BuildingState.FINISHED || state === BuildingState.FINISHED_UNDER_RECONSTRUCTION
          }
          allowOnlySvkCountry
        />
      </Card>

      <ClientDrawerForm<ClientFormType>
        open={clientFormOpen}
        client={policyHolder}
        initialIdentifier={getPolicyHolderFormIdentifier()}
        formType={ClientFormType.POLICY_HOLDER}
        violationErrors={props.clientsViolationErrors}
        onFormSubmit={handleClientFormSubmit}
      />
    </>
  );
};
